import React from 'react';
import RichText from 'components/RichText';
import Content from 'components/_ui/Content';
import styled from '@emotion/styled';
import dimensions from 'styles/dimensions';
import { Inner } from 'styles/structure';
import { Rubric } from 'styles/typography';
import colors from 'styles/colors';
import Illustration from 'components/_ui/Illustration';
import ReadMore from 'components/ReadMore';

const ContentListContainer = styled.div`
  position: relative;
  display: block;
  padding: 6.4rem 0 5.6rem;
  background-color: ${colors.orange100};
  
  & + * {
    margin-top: 6.4rem;
  }
  
  header {
    margin-bottom: 5.6rem;
    
    p {
      max-width: 74.8rem;
      margin-left: auto;
      margin-right: auto;
    }
    
    h2 {
      & + * {
        margin-top: 1.6rem;
      }
    }
    
    em {
      position: relative;
      font-style: normal;
      white-space: nowrap;

      &::after {
        content: '';
        position: absolute;
        bottom: -0.2rem;
        left: 0;
        width: 100%;
        height: 0.2rem;
        background-color: ${colors.teal800};
      }
    }
  }
  
  &.ContentList--about,
  &.ContentList--homepage {
    background-color: #fff;
  }
  
  @media(min-width: ${dimensions.tabletLandscapeUp}px) {
    padding: 12rem 0 8rem;
    
    & + * {
      margin-top: 10.4rem;
    }
    
    header {
      text-align: center;
      margin-bottom: 12rem;
    }
  }
  
  @media(min-width: ${dimensions.desktopUp}px) {
    p {
      font-size: 2.1rem;
    }
  }
`;

const ContentListItems = styled.ul`
  .ContentList--numbered & {
    counter-reset: listCounter;
    
    > li {
      counter-increment: listCounter;
    
      > *:last-child {
        &::before {
          content: counter(listCounter);
          display: block;
          margin-bottom: 0.8rem;
          color: ${colors.teal800};
          font-size: 4.2rem;
          font-weight: 400;
        }
      }
    }
  }
  
  > li {    
    & + * {
      margin-top: 4.8rem;
    }
  }
  
  @media(min-width: ${dimensions.tabletLandscapeUp}px) {    
    > li {
      display: flex;
      align-items: center;

      & + * {
        margin-top: 10.4rem;
      }
      
      &:nth-of-type(even) {
        > :first-of-type {
          order: 2;
          margin-right: 0;
          margin-left: 5.6vw;
        }
      }
    }
  }
`;

const ItemImage = styled.div`
  display: block;
  width: 100%;
  max-width: 40rem;
  
  & + * {
    margin-top: 2.4rem;
  }
  
  img {
    width: 100%;
  }
  
  @media(min-width: ${dimensions.tabletLandscapeUp}px) {
    flex: 2 1 50%;
    margin-right: 5.6vw;
  } 
  
  @media(min-width: ${dimensions.desktopUp}px) {
    max-width: none;
  }
`;

const ItemContent = styled.div`
  h4 {
    & + * {
      margin-top: 1.6rem;
    }
  }
  
  > div {
    & + * {
      margin-top: 2.4rem;
    } 
  }
  
  @media(min-width: ${dimensions.tabletLandscapeUp}px) {
    flex: 1 1 50%;
    padding: 4rem;
  }
`;

const ContentList = ({ intro, heading, content, items, variant }) => {
  let renderedHeading;

  if (heading && heading[0] && heading[0].text) {
    renderedHeading = (<RichText render={heading}/>);

  } else if (heading) {
    renderedHeading = (<h2>{heading}</h2>);
  }

  return (
    <ContentListContainer className={variant && `ContentList--${variant}`}>
      <Inner>
        {(intro || heading || content) && (
          <header>
            {intro && <Rubric>{intro}</Rubric>}
            {renderedHeading && renderedHeading}

            {content && <Content content={content}/>}
          </header>
        )}

        <ContentListItems>
          {items && items.map((item, i) => (
            <li key={i}>
              <ItemImage>
                {item.illustration && (
                  <Illustration source={item.illustration} poster={item.image}/>
                )}

                {(!item.illustration && item.image) && (
                  <img src={item.image.url} alt={item.image.alt} loading="lazy" />
                )}
              </ItemImage>

              <ItemContent>
                {item.heading && <h4>{item.heading}</h4>}
                {item.heading1 && <h4>{item.heading1}</h4>}

                {item.content && <Content content={item.content}/>}

                {(item.additional_content && item.additional_content[0].text.length > 0) && <ReadMore content={item.additional_content}/>}
              </ItemContent>
            </li>
          ))}
        </ContentListItems>
      </Inner>
    </ContentListContainer>
  );
};

export default ContentList;
